import {
  endBefore,
  equalTo,
  get,
  limitToLast,
  orderByChild,
  orderByValue,
  push,
  query,
  ref,
  remove,
  runTransaction,
  set,
  type Query,
  update,
} from 'firebase/database';
import { db } from './app';

/**
 * Petite couche au-dessus de la Realtime Database.
 *
 * Elle fait trois choses : rendre les lectures typees, transformer les
 * objets `{ cle: valeur }` de Firebase en tableaux exploitables, et
 * centraliser les erreurs pour que l'interface affiche des phrases
 * francaises plutot que des codes Firebase.
 */

/** Erreur remontee a l'interface, deja traduite. */
export class SiroccoDataError extends Error {
  readonly code: string;
  readonly field?: string;

  constructor(message: string, code = 'unknown', field?: string) {
    super(message);
    this.name = 'SiroccoDataError';
    this.code = code;
    this.field = field;
  }
}

/** Lit une valeur unique, ou `null` si le chemin est vide. */
export async function readValue<T>(path: string): Promise<T | null> {
  const snapshot = await get(ref(db(), path));
  return snapshot.exists() ? (snapshot.val() as T) : null;
}

/**
 * Lit une collection et rend un tableau, chaque element portant sa cle
 * Firebase dans `id`. C'est la forme attendue partout dans l'app.
 */
/**
 * Les N plus hauts d'une branche de nombres.
 *
 * `orderByValue` trie cote serveur : on ne telecharge que ce qu'on
 * affiche. Un classement lu en entier grossirait avec la ville, et c'est
 * le volume telecharge qui fait la facture d'une base temps reel.
 */
export async function readTopValues(path: string, combien: number): Promise<[string, number][]> {
  const snap = await get(query(ref(db(), path), orderByValue(), limitToLast(combien)));
  const lignes: [string, number][] = [];
  snap.forEach((enfant) => {
    const valeur = enfant.val();
    if (typeof valeur === 'number' && enfant.key) lignes.push([enfant.key, valeur]);
  });
  // `limitToLast` rend du plus petit au plus grand : on retourne la liste.
  return lignes.reverse();
}

export async function readList<T extends object>(
  path: string,
  options: {
    orderBy?: string;
    limit?: number;
    /**
     * Ou reprendre, pour la page suivante : tout ce qui precede
     * strictement cette valeur. La cle accompagne la valeur pour departager
     * deux enregistrements de la meme milliseconde — sans elle, `endBefore`
     * les ecarterait tous les deux et la page sauterait des lignes.
     */
    before?: { value: number; key: string };
    /** Seulement ceux dont le champ trie vaut ceci : les posts d'une association. */
    egalA?: string;
  } = {},
): Promise<(T & { id: string })[]> {
  let target: Query = ref(db(), path);
  if (options.orderBy) target = query(target, orderByChild(options.orderBy));
  if (options.egalA !== undefined) target = query(target, equalTo(options.egalA));
  if (options.before) target = query(target, endBefore(options.before.value, options.before.key));
  if (options.limit) target = query(target, limitToLast(options.limit));

  const snapshot = await get(target);
  if (!snapshot.exists()) return [];

  const rows: (T & { id: string })[] = [];
  // forEach preserve l'ordre du tri demande, contrairement a val().
  snapshot.forEach((child) => {
    rows.push({ ...(child.val() as T), id: child.key! });
  });
  return rows;
}

/**
 * Lit un ensemble de cles booleennes (`{ id: true }`) et rend la liste
 * des identifiants. C'est la forme de tous les index de l'application :
 * signets, amis, participants.
 */
export async function readKeys(path: string): Promise<string[]> {
  const snapshot = await get(ref(db(), path));
  if (!snapshot.exists()) return [];
  return Object.keys(snapshot.val() as Record<string, unknown>);
}

/**
 * Comme `readKeys`, mais une lecture refusee rend une liste vide.
 *
 * Reserve aux index qui n'ajoutent qu'un filtre — les membres bloques,
 * par exemple. Tant que leur regle n'est pas deployee, la racine
 * `.read: false` refuse tout, et une seule branche manquante ferait
 * tomber l'ecran entier avec un "Permission denied" opaque. Mieux vaut
 * un feed sans filtre qu'un feed sans posts.
 */
export async function readOptionalKeys(path: string): Promise<string[]> {
  try {
    return await readKeys(path);
  } catch {
    return [];
  }
}

/**
 * Meme tolerance que `readOptionalKeys`, pour une liste.
 *
 * Reserve aux balayages qui passent sur tous les serveurs — mes posts,
 * mes sorties — depuis qu'un serveur peut etre prive : celui dont on n'a
 * pas la cle refuse la lecture, et ce refus emportait l'ecran entier
 * avec un « Permission denied » qui ne disait rien. Un serveur ferme ne
 * doit rien apporter, pas tout faire tomber.
 */
export async function readOptionalList<T extends object>(
  path: string,
): Promise<(T & { id: string })[]> {
  try {
    return await readList<T>(path);
  } catch {
    return [];
  }
}

/** Meme tolerance que `readOptionalKeys`, pour une valeur unique. */
export async function readOptionalValue<T>(path: string): Promise<T | null> {
  try {
    return await readValue<T>(path);
  } catch {
    return null;
  }
}

/** Vrai si le chemin porte une valeur. */
export async function exists(path: string): Promise<boolean> {
  const snapshot = await get(ref(db(), path));
  return snapshot.exists();
}

export async function writeValue(path: string, value: unknown): Promise<void> {
  await set(ref(db(), path), value);
}

export async function updateValues(patch: Record<string, unknown>): Promise<void> {
  await update(ref(db()), patch);
}

export async function removeValue(path: string): Promise<void> {
  await remove(ref(db(), path));
}

/** Ajoute un enfant sous une cle generee par Firebase, et rend cette cle. */
export async function pushValue(path: string, value: unknown): Promise<string> {
  const created = push(ref(db(), path));
  await set(created, value);
  return created.key!;
}

/** Reserve une cle sans encore ecrire, pour composer une mise a jour groupee. */
export function newKey(path: string): string {
  return push(ref(db(), path)).key!;
}

/**
 * Incremente un compteur sans risque de perte.
 * Deux likes simultanes doivent compter pour deux : une transaction est
 * le seul moyen de le garantir cote Realtime Database.
 */
export async function bumpCounter(path: string, delta: number): Promise<number> {
  const result = await runTransaction(ref(db(), path), (current) => (current ?? 0) + delta);
  return (result.snapshot.val() as number | null) ?? 0;
}

/**
 * Lecture puis ecriture en un seul geste, sans qu'un autre appareil
 * puisse se glisser entre les deux.
 *
 * `bumpCounter` couvre le cas courant — ajouter a un nombre. Celui-ci
 * sert quand la nouvelle valeur se calcule sur l'ancienne : un solde et
 * le niveau qui s'en deduit doivent se poser ensemble, sinon il existe un
 * instant ou l'un ne correspond plus a l'autre.
 *
 * Rendre `undefined` depuis `next` annule l'ecriture : c'est ainsi qu'on
 * refuse une attribution deja faite. `committed` dit ce qu'il en est.
 */
export async function transact<T>(
  path: string,
  next: (current: T | null) => T | null | undefined,
): Promise<{ committed: boolean; value: T | null }> {
  const result = await runTransaction(ref(db(), path), (current) => next(current as T | null));
  return { committed: result.committed, value: (result.snapshot.val() as T | null) ?? null };
}

/**
 * Charge plusieurs enregistrements par identifiant, en une passe.
 * La Realtime Database n'a pas de "where in" : on lit chaque cle, mais
 * en parallele, et on met en cache pour la duree de l'appel.
 */
export async function readMany<T extends object>(
  pathOf: (id: string) => string,
  ids: string[],
): Promise<Map<string, T>> {
  const unique = [...new Set(ids)];
  const rows = await Promise.all(unique.map((id) => readValue<T>(pathOf(id))));
  const map = new Map<string, T>();
  unique.forEach((id, index) => {
    const row = rows[index];
    if (row) map.set(id, row);
  });
  return map;
}
